import {
  isActiveMatchStatus,
  isCompletedStatus,
  isLiveStatus,
  showsLiveBoardStatus,
} from "@/entities/bracket/lib/match-status";
import type { Tournament } from "@/entities/tournament/model/types";

/**
 * KAN-365: что показывать на странице матча.
 *
 * Логика жила инлайном в компоненте страницы и потому не закреплялась тестом — а
 * ошибиться тут легко, потому что решение зависит сразу от трёх вещей: режима
 * продвижения победителя, статуса матча и того, кто смотрит.
 *
 * Живой прогон показал цену такой ошибки: капитан турнира с подтверждением по
 * реплеям открыл матч и увидел панель телеметрии (которая собирается только
 * десктоп-приложением и потому была пуста) и предложение поставить это самое
 * приложение — при полном отсутствии того, ради чего он пришёл.
 */

export type MatchPanelsInput = {
  /** `tournament.manualWinnerMode` — режим продвижения победителя. */
  mode: Tournament["manualWinnerMode"];
  /** Сырой статус матча с бэкенда. */
  status?: string | null;
  /** Смотрящий — капитан одной из команд матча. */
  isCaptain: boolean;
  /** Десктоп-приложение запущено у смотрящего. */
  isTauriHere: boolean;
  /** Смотрящий играет этот матч (капитан или игрок состава). */
  isPlaying?: boolean;
  /** Смотрящий — организатор турнира. */
  isOrganizer?: boolean;
};

export type MatchPanels = {
  /** Панель live-телеметрии матча. */
  liveBoard: boolean;
  /** Карточка загрузки .rofl для подтверждения победы. */
  replayConfirm: boolean;
  /**
   * Запасная кнопка загрузки реплея — на случай, когда основной путь (Discord)
   * не сработал (лимит бота ~25 МБ на длинных играх), а `replayConfirm` капитану
   * не показан, потому что турнир не в режиме `replay`. Живой случай: капитаны
   * не понимали, куда деть реплей, когда бот отказал, и матч остался без
   * статистики насовсем.
   */
  replayUpload: boolean;
  /** Объяснение, почему загрузка недоступна прямо сейчас. */
  replayPending: boolean;
  /** Баннер «поставь десктоп-приложение». */
  downloadApp: boolean;
  /** KAN-167: карточка «заявить/подтвердить результат» капитаном (режим manual). */
  captainResult: boolean;
  /** Чек-лист шагов матча (лобби, чемпион-селект, отправка результата). */
  checklist: boolean;
};

export function getMatchPanels(input: MatchPanelsInput): MatchPanels {
  const { mode, status, isCaptain, isTauriHere } = input;
  const isPlaying = input.isPlaying ?? isCaptain;

  const isReplayMode = mode === "replay";
  const live = isLiveStatus(status);
  const completed = isCompletedStatus(status);

  return {
    // Телеметрию собирает и пушит десктоп-приложение. Оно есть только у того, кто
    // сидит в самом приложении: в вебе панель годами показывала «Live-данные ещё
    // не пришли» и при этом дёргала /live-data раз в 5 секунд плюс держала
    // SignalR-подписку. Пустой борд читается как сломанная страница, поэтому
    // показываем его только там, где данные вообще могут появиться.
    liveBoard: showsLiveBoardStatus(status) && !isReplayMode && isTauriHere,

    // Капитану загрузка предлагается только там, где она и есть основной путь.
    // При `manual`/`desktop` результат проставляет организатор — обещать капитану
    // действие, которого от него не ждут, значит сбивать с толку (KAN-354).
    replayConfirm: isReplayMode && isCaptain && live,

    // Запасной путь для manual/desktop: там `replayConfirm` капитану намеренно не
    // показывают (KAN-354 — не его основной путь), но загрузка на сайте технически
    // доступна и там (бэкенд не смотрит на режим — только на «капитан + матч в
    // эфире»). Организатору тот же путь на этой странице раньше не показывался
    // вовсе — за ним приходилось идти в модалку результата в сетке.
    // Проверки «уже загружен ли реплей» тут нет: подтверждение само переводит матч
    // из Live, так что «матч ещё живой» и есть «реплей ещё не подтверждён» — тот
    // же принцип, что уже применён в replayConfirm.
    replayUpload:
      live && ((isCaptain && !isReplayMode) || Boolean(input.isOrganizer)),

    // С KAN-365 матч уходит в эфир сам, так что до эфира капитан теперь висит
    // только у матча, назначенного на более позднее время. Причину всё равно
    // показываем: молчание в этом месте и было дефектом.
    replayPending: isReplayMode && isCaptain && !live && !completed,

    // Предлагать десктоп там, где он не нужен, — реклама не по адресу.
    // Условие полное: раньше половину решал предикат, половину — страница, и
    // прочитать правило целиком было негде.
    // Зрителю и организатору приложение не нужно (они ничего в нём не делают) —
    // им баннер занимал место на странице, которую открывают ради «кто с кем».
    downloadApp:
      !isTauriHere && !isReplayMode && isPlaying && isActiveMatchStatus(status),

    // KAN-167: двустороннее подтверждение капитанами — в режиме manual, где иначе
    // результат ставит только организатор. В replay/desktop есть свои пути (реплей/Tauri).
    captainResult: mode === "manual" && isCaptain && live,

    // Чек-лист описывает шаги десктоп-флоу (клиент запущен, лобби создано,
    // результат отправлен). Вне приложения он ничего не отражает и ни к чему не
    // ведёт, а зрителю не адресован вовсе.
    checklist: isTauriHere && isPlaying,
  };
}
